
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPasswordForm() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handlePasswordRecovery = async () => {
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const type = searchParams.get('type');
      
      console.log("URL params:", { accessToken: !!accessToken, refreshToken: !!refreshToken, type });

      // Se temos tokens na URL, isso significa que viemos do link do email
      if (accessToken && refreshToken && type === 'recovery') {
        try {
          // Definir a sessão com os tokens da URL
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          console.log("Session set result:", { data, error });

          if (error) {
            console.error("Error setting session:", error);
            toast({
              title: "Link expirado",
              description: "Este link de recuperação expirou. Solicite um novo.",
              variant: "destructive",
            });
            navigate("/auth");
            return;
          }

          if (data.session) {
            console.log("Session created successfully");
            setIsValidSession(true);
            setIsVerifying(false);
            toast({
              title: "Link válido",
              description: "Agora você pode definir sua nova senha.",
            });
          }
        } catch (error) {
          console.error("Error handling recovery:", error);
          toast({
            title: "Erro de verificação",
            description: "Não foi possível verificar o link de recuperação.",
            variant: "destructive",
          });
          navigate("/auth");
        }
      } else {
        // Verificar se já temos uma sessão ativa (caso o usuário já tenha clicado no link)
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          console.log("Current session check:", { session: !!session, error });

          if (error) {
            console.error("Error getting session:", error);
            toast({
              title: "Erro de verificação",
              description: "Não foi possível verificar a sessão.",
              variant: "destructive",
            });
            navigate("/auth");
            return;
          }

          if (session) {
            console.log("Valid session found");
            setIsValidSession(true);
            setIsVerifying(false);
            toast({
              title: "Sessão válida",
              description: "Agora você pode definir sua nova senha.",
            });
          } else {
            console.log("No valid session or tokens found");
            toast({
              title: "Link inválido",
              description: "Este link de recuperação é inválido ou expirou.",
              variant: "destructive",
            });
            navigate("/auth");
          }
        } catch (error) {
          console.error("Error checking session:", error);
          toast({
            title: "Erro de verificação",
            description: "Não foi possível verificar a sessão.",
            variant: "destructive",
          });
          navigate("/auth");
        }
      }

      setIsVerifying(false);
    };

    handlePasswordRecovery();
  }, [navigate, searchParams]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Senhas não coincidem",
        description: "As senhas digitadas não são iguais.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Senha muito curta",
        description: "A senha deve ter pelo menos 6 caracteres.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      toast({
        title: "Senha alterada com sucesso!",
        description: "Sua senha foi redefinida. Você pode fazer login agora.",
      });

      // Sign out the user and redirect to login
      await supabase.auth.signOut();
      navigate("/auth");
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast({
        title: "Erro ao alterar senha",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading while verifying token
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-black text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto bg-gray-900 rounded-lg p-8 border border-gray-800">
            <div className="text-center">
              <div className="text-6xl mb-4">🔐</div>
              <h2 className="text-2xl font-bold mb-4 text-krecao-yellow">Verificando Link</h2>
              <p className="text-gray-400">Verificando link de recuperação...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show form if session is valid
  if (isValidSession) {
    return (
      <div className="min-h-screen bg-black text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto bg-gray-900 rounded-lg p-8 border border-gray-800">
            <div className="mb-8 text-center">
              <div className="text-6xl mb-4">🔐</div>
              <h2 className="text-3xl font-bold mb-2 text-krecao-yellow">Nova Senha</h2>
              <p className="text-gray-400">
                Digite sua nova senha
              </p>
            </div>

            <form onSubmit={handleResetPassword}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Digite sua nova senha"
                      required
                      className="bg-gray-800 border-gray-700 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirme sua nova senha"
                      required
                      className="bg-gray-800 border-gray-700 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-krecao-red hover:bg-krecao-red/90"
                  disabled={loading}
                >
                  {loading ? "Alterando..." : "Alterar Senha"}
                </Button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="text-krecao-yellow hover:underline text-sm"
              >
                Voltar ao login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // This should not be reached, but just in case
  return null;
}
